<template>
  <!--
    PoolaSupplierRun — the READ-ONLY supplier composition (FE plan Section 2). The
    supplier sibling of PoolaRun: same run header + suppliers filter + offers list
    with infinite load-more, but NO buy path — it never imports the claim sheet,
    the balance pill, or useWallets, and never calls usePoolaOffers().claim().
    A CONTENT FRAGMENT — never its own <f7-page> (the host owns the page shell;
    nesting a page here would clip the active fragment, playbook Section 5 row 17).

    Layout: an IN-CONTENT segment window (D5) — «العروض» (all run offers,
    read-only) | «منتجاتي» (the supplier's own items + their claimed counts) |
    an optional third «التقرير النهائي» segment, shown ONLY once a closed deal
    is resolvable (useSupplierDealReport's dealId/notAvailable — same v1
    most-recent-ENDED-run resolution the finale card uses). The segment control
    is rendered in CONTENT, NOT a <f7-subnavbar>, so the shared PoolaPage
    navbar/shell stays untouched (and avoids the page-with-subnavbar padding
    trap).

    phase: "scheduled" → locked preview; "active" → live (still read-only for a
    supplier); "ended" → greyed historical read.

    Offers + facets come from usePoolaOffers(dealId); `myOffers` is the
    server-confirmed isMine subset for the منتجاتي segment.
  -->
  <div class="poola-supplier-run" :data-phase="phase">
    <poola-run-header :phase="phase" :deal="deal" :server-offset="serverOffset"></poola-run-header>

    <!-- Read-only banner: a supplier views the run but cannot claim (suppliers
         POST …/claim → 403). -->
    <div class="poola-supplier-run__banner" role="note">
      {{ t('poola.supplier.readOnlyBanner') }}
    </div>

    <!-- In-content segment control (D5) — العروض | منتجاتي | التقرير النهائي. -->
    <div class="poola-supplier-run__segmented">
      <f7-segmented strong>
        <f7-button :active="activeSegment === 'offers'" @click="switchSegment('offers')">{{
          t('poola.supplier.segments.offers')
        }}</f7-button>
        <f7-button :active="activeSegment === 'mine'" @click="switchSegment('mine')">{{
          t('poola.supplier.segments.mine')
        }}</f7-button>
        <f7-button
          v-if="showReportSegment"
          :active="activeSegment === 'report'"
          @click="switchSegment('report')"
        >{{ t('poola.report.tab') }}</f7-button>
      </f7-segmented>
    </div>

    <!-- ── التقرير النهائي: a wholly separate data source (closed-deal
         post-mortem), so it bypasses the offersAll-based run-level states
         below — the panel owns its own loading/empty/forbidden states. -->
    <template v-if="activeSegment === 'report'">
      <supplier-deal-report-panel></supplier-deal-report-panel>
    </template>

    <template v-else>
      <!-- Run-level states (offersAll-based) gate BOTH segments so a stale
           supplier chip or an empty منتجاتي can never strand us in a
           skeleton/empty. -->
      <poola-error v-if="error && !offersAll.length" @retry="loadInitial"></poola-error>

      <poola-skeleton v-else-if="loading && !offersAll.length"></poola-skeleton>

      <template v-else-if="!offersAll.length">
        <div class="poola-supplier-run__empty">
          <p class="poola-supplier-run__empty-title">{{ t('poola.buy.empty.header') }}</p>
          <p class="poola-supplier-run__empty-body">{{ t('poola.buy.empty.body') }}</p>
        </div>

        <!-- No upcoming offers yet: the full recap card under the empty text, in
             the «العروض» segment only (this empty state gates both segments). -->
        <poola-last-run-card
          v-if="showLastRun && activeSegment === 'offers'"
          :run="lastRun"
          :see-all-href="lastRunHref"
        ></poola-last-run-card>
      </template>

      <template v-else>
        <!-- ── العروض: all run offers, read-only ───────────────────────────── -->
        <template v-if="activeSegment === 'offers'">
          <!-- The last finished Poola, COMPACT, above the upcoming offers (the
               scheduled phase only; an empty run shows the full card instead). -->
          <poola-last-run-card
            v-if="showLastRun"
            compact
            :run="lastRun"
            :see-all-href="lastRunHref"
          ></poola-last-run-card>

          <!-- Suppliers rail — only worth showing once ≥1 real supplier chip
               exists alongside «الكل» (client-side facet, D8). -->
          <poola-supplier-filter
            v-if="showFilter"
            :suppliers="suppliers"
            :active-supplier="activeSupplier"
            @set-supplier="setSupplier"
          ></poola-supplier-filter>

          <!-- Plain container (NOT f7-list): PoolaOfferCard is a self-contained
               f7-card. read-only → the card renders no claim CTA + emits no
               claim. -->
          <div class="poola-supplier-run__list">
            <poola-offer-card
              v-for="offer in offers"
              :key="offer.id"
              :offer="offer"
              :phase="phase"
              read-only
            ></poola-offer-card>
          </div>

          <!-- Infinite load-more sentinel (IntersectionObserver) + a
               more-spinner. Light payload → plain list, no virtualization. -->
          <div ref="sentinelRef" class="poola-supplier-run__sentinel" aria-hidden="true" />
          <div v-if="isLoadingMore" class="poola-supplier-run__more">
            <f7-preloader></f7-preloader>
          </div>
        </template>

        <!-- ── منتجاتي: the supplier's own items + their claimed counts ─────── -->
        <template v-else>
          <div v-if="myOffers.length" class="poola-supplier-run__list">
            <div v-for="offer in myOffers" :key="offer.id" class="poola-supplier-run__mine-item">
              <poola-offer-card :offer="offer" :phase="phase" read-only></poola-offer-card>
              <!-- Emphasize how many members have bought this item of mine. -->
              <p class="poola-supplier-run__claimed-summary">
                {{ t('poola.supplier.claimedSummary', { n: offer.piecesClaimed }) }}
              </p>
            </div>
          </div>

          <!-- منتجاتي empty sub-state — DO NOT hide the section; an empty
               منتجاتي is a valid state (none of my items are in this run
               yet). -->
          <div v-else class="poola-supplier-run__mine-empty">
            <p class="poola-supplier-run__empty-title">
              {{ t('poola.supplier.mine.empty.header') }}
            </p>
            <p class="poola-supplier-run__empty-body">
              {{ t('poola.supplier.mine.empty.body') }}
            </p>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PoolaError from './PoolaError.vue';
import PoolaLastRunCard from './PoolaLastRunCard.vue';
import PoolaOfferCard from './PoolaOfferCard.vue';
import PoolaRunHeader from './PoolaRunHeader.vue';
import PoolaSkeleton from './PoolaSkeleton.vue';
import PoolaSupplierFilter from './PoolaSupplierFilter.vue';
import SupplierDealReportPanel from './report/SupplierDealReportPanel.vue';
import { isReplayOn } from '@/collector/previewReplay.js';
import { usePoolaOffers } from '@/composables/usePoolaOffers.js';
import { useSupplierDealReport } from '@/composables/useSupplierDealReport.js';

const props = defineProps({
  phase: {
    type: String,
    default: 'active',
    validator: (v) => ['scheduled', 'active', 'ended'].includes(v),
  },
  // The deserialised poola-deals resource (id + flattened attributes).
  deal: {
    type: Object,
    default: null,
  },
  // serverNow − clientNow (ms) from usePoolaDeal — passed through to the header
  // countdown so a skewed phone clock doesn't flip the run early/late.
  serverOffset: {
    type: Number,
    default: 0,
  },
  // usePoolaDeal.previousRun — the last finished run, passed by PoolaPage only on
  // the COMING branch. null (the default) leaves every screen exactly as before.
  lastRun: {
    type: Object,
    default: null,
  },
  // Where the recap card's link goes (the last-Poola page). null → no link.
  lastRunHref: {
    type: String,
    default: null,
  },
});

// deal-not-active is declared for parity with PoolaRun's host wiring
// (@deal-not-active="refresh"). A read-only supplier never claims, so this never
// fires here — but declaring it keeps the host listener a real component emit
// rather than a no-op fallthrough attribute on the root <div>.
defineEmits(['deal-not-active']);

const { t } = useI18n({ useScope: 'global' });

// filter[deal] is required (a blank id 422s); a getter keeps it reactive if the
// deal id ever changes under us.
const dealId = computed(() => props.deal?.id ?? null);

// NOTE: claim() is intentionally NOT destructured — the supplier path is
// read-only and must never reach the money move.
const {
  offers,
  offersAll,
  myOffers,
  suppliers,
  activeSupplier,
  setSupplier,
  hasMore,
  isLoadingMore,
  loading,
  error,
  loadInitial,
  loadMore,
} = usePoolaOffers(dealId);

// Show the rail only when there's a real supplier chip beyond «الكل».
const showFilter = computed(() => suppliers.value.length > 1);

// Last-Poola recap card: scheduled phase + a previous run handed down by the host.
// Loading or a first-load error with no offers renders no card.
const showLastRun = computed(() => props.phase === 'scheduled' && props.lastRun != null);

// ── التقرير النهائي gate ──────────────────────────────────────────────────────
// The panel is fully self-contained (owns its own usePoolaDeal + fetch), so we
// only need dealId/notAvailable here to decide whether the THIRD segment button
// is worth showing at all — no closed deal ⇒ no dead segment (P4-A rule 3).
const { dealId: reportDealId, notAvailable: reportNotAvailable } = useSupplierDealReport();
const showReportSegment = computed(() => reportDealId.value != null && !reportNotAvailable.value);

// ── Segment state ─────────────────────────────────────────────────────────────
const activeSegment = ref('offers'); // 'offers' | 'mine' | 'report'

function switchSegment(seg) {
  if (activeSegment.value === seg) return;
  activeSegment.value = seg;
  if ('vibrate' in navigator) navigator.vibrate(10);
}

// Safety net: if the segment button disappears (e.g. the underlying deal
// re-resolves out of ENDED) while the report is open, fall back to العروض
// instead of stranding the viewer on a segment with no visible button.
watch(showReportSegment, (visible) => {
  if (!visible && activeSegment.value === 'report') activeSegment.value = 'offers';
});

// ── Infinite load-more (IntersectionObserver) ────────────────────────────────
// Same wiring as PoolaRun: the sentinel only exists in the العروض segment, so
// the watch re-binds the observer when the segment mounts/unmounts it.
const sentinelRef = ref(null);
let observer = null;

// An offline supplier preview (supplier-preview-offline L-J): the phone's copy has no next page.
const replayOn = isReplayOn();

function maybeLoadMore() {
  if (replayOn) return;
  if (hasMore.value && !loading.value && !isLoadingMore.value) loadMore();
}

function ensureObserver() {
  if (observer || typeof IntersectionObserver === 'undefined') return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) maybeLoadMore();
    },
    { rootMargin: '200px' },
  );
}

watch(sentinelRef, (el) => {
  ensureObserver();
  if (!observer) return;
  observer.disconnect();
  if (el) observer.observe(el);
});

onMounted(() => {
  loadInitial();
  // No wallet read on the supplier path — there is no affordability surface.
});

onUnmounted(() => {
  observer?.disconnect();
  observer = null;
});

// Exposed for the host / tests to drive load-more without the observer.
defineExpose({ loadMore: maybeLoadMore });
</script>

<style scoped>
.poola-supplier-run {
  padding-bottom: 24px;
}

/* ── Read-only banner ─────────────────────────────────────────────────────── */
.poola-supplier-run__banner {
  margin: 0 16px 12px;
  padding: 10px 14px;
  background: var(--poolit-primary-50);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-md);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-medium);
  text-align: center;
  line-height: var(--poolit-line-height-body);
}

/* ── In-content segment control ───────────────────────────────────────────── */
.poola-supplier-run__segmented {
  max-width: 400px;
  margin: 0 auto 8px;
  padding: 0 16px;
  direction: ltr;
}

/* ── Offers / mine list ───────────────────────────────────────────────────── */
.poola-supplier-run__list {
  display: flex;
  flex-direction: column;
}

.poola-supplier-run__mine-item {
  display: flex;
  flex-direction: column;
}

.poola-supplier-run__claimed-summary {
  margin: -4px 16px 12px;
  padding: 6px 12px;
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-full);
  font-size: var(--poolit-font-size-small);
  font-weight: var(--poolit-font-weight-semibold);
  text-align: center;
}

.poola-supplier-run__sentinel {
  height: 1px;
}

.poola-supplier-run__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* ── Empty states (run-level + منتجاتي) ───────────────────────────────────── */
.poola-supplier-run__empty,
.poola-supplier-run__mine-empty {
  text-align: center;
  padding: 48px 28px;
}

.poola-supplier-run__empty-title {
  color: var(--poolit-text-primary);
  font-size: var(--poolit-font-size-body);
  font-weight: var(--poolit-font-weight-semibold);
  margin: 0 0 8px;
}

.poola-supplier-run__empty-body {
  color: var(--poolit-text-muted);
  font-size: var(--poolit-font-size-small);
  margin: 0;
  max-width: 300px;
  margin-inline: auto;
}

/* Ended runs render greyed + read-only (locked design, mirrors PoolaRun). */
.poola-supplier-run[data-phase='ended'] {
  opacity: 0.55;
}
</style>
